import { useEffect, useState } from "react";
import { api, euros } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import { fmtDate } from "./shared";

const CHIPS = [["", "Tous"], ["sent", "Envoyés"], ["pending", "En attente"], ["opened", "Ouverts"], ["not_opened", "Non ouverts"], ["clicked", "Cliqués"], ["registered", "Inscrits"], ["purchased", "Acheteurs"], ["unsubscribed", "Désinscrits"], ["invalid", "Adresses invalides"], ["error", "Erreurs / ignorés"]];
const STATE = { pending: "En attente", sent: "Envoyé", opened: "Ouvert", clicked: "A cliqué", registered: "Inscrit", purchased: "A acheté", unsubscribed: "Désinscrit", error: "Erreur", invalid: "Adresse invalide", ignored: "Ignoré" };

// Suivi e-mail par e-mail d'une campagne (filtres, recherche, export Excel).
export const ContactsReport = ({ cid, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/admin/email-campaigns/${cid}/contacts`, { params: { status, q } }).then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [cid, status, q, refreshKey]);

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/admin/email-campaigns/${cid}/export.xlsx`, { params: { status, q }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `suivi-campagne-${cid.slice(0, 8)}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`campaign-contacts-${cid}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-white">Suivi e-mail par e-mail <span className="ml-1 text-xs font-normal text-muted-foreground" data-testid="report-total">{total.toLocaleString("fr-FR")} adresse(s)</span></p>
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher" className="h-8 w-56 rounded-md border border-white/15 bg-black/40 pl-7 pr-2 text-xs text-white outline-none" data-testid={`report-search-${cid}`} />
        </div>
        <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={exportXlsx} data-testid={`report-export-${cid}`}><Download size={13} className="mr-1" /> Excel</Button>
      </div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {CHIPS.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} className={`rounded-full border px-2.5 py-1 text-[11px] ${status === k ? "border-sky-400 bg-sky-500 text-white" : "border-white/15 text-muted-foreground hover:text-white"}`} data-testid={`report-chip-${k || "all"}`}>{l}</button>
        ))}
      </div>
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead className="sticky top-0 bg-[#141110] text-muted-foreground">
            <tr><th className="p-2">E-mail</th><th className="p-2">État</th><th className="p-2">Envoyé</th><th className="p-2">1re ouverture</th><th className="p-2">Ouv.</th><th className="p-2">1er clic</th><th className="p-2">Clics</th><th className="p-2">Inscrit</th><th className="p-2">Acheté</th><th className="p-2">Désinscrit</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid="report-row">
                <td className="p-2 text-white" title={c.error || ""}>{c.email}</td>
                <td className={`p-2 ${c.status === "invalid" || c.status === "error" ? "text-rose-300" : ""}`}>{STATE[c.status] || c.status}</td>
                <td className="p-2">{fmtDate(c.sent_at || c.errored_at)}</td>
                <td className="p-2">{fmtDate(c.opened_at)}</td>
                <td className="p-2">{c.open_count || (c.opened_at ? 1 : 0)}</td>
                <td className="p-2">{fmtDate(c.clicked_at)}</td>
                <td className="p-2">{c.click_count || (c.clicked_at ? 1 : 0)}</td>
                <td className="p-2">{c.registered_at ? "Oui" : "Non"}</td>
                <td className="p-2">{c.purchased_at ? euros(c.revenue_cents || 0) : "—"}</td>
                <td className="p-2">{c.unsubscribed_at ? fmtDate(c.unsubscribed_at) : "—"}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className="p-3 text-center text-muted-foreground">Aucun contact.</td></tr>}
          </tbody>
        </table>
      </div>
      {total > rows.length && <p className="mt-2 text-[11px] text-muted-foreground">{rows.length} affiché(s) sur {total.toLocaleString("fr-FR")} · tout est dans l'export Excel.</p>}
    </div>
  );
};
